import { StyleSheet, View } from "react-native";

import { format, parseISO } from "date-fns";
import pluralize from "pluralize";

import { AnimatedPlaceholder, Typography } from "@smartrent/ui";

import { TourStatus, UnitType } from "@/types";

interface IntroProps {
  disableDoubleBookings: boolean;
  tourStatus: TourStatus;
  tourStart: string;
  tourEnd: string;
  units: UnitType[];
  prospectFirstName: string | null;
}

export function getTourTitle({
  disableDoubleBookings,
  title = "Self Tour",
  privateTitlePrefix = "Private",
}: {
  disableDoubleBookings: boolean;
  title?: string;
  privateTitlePrefix?: string;
}) {
  return disableDoubleBookings ? `${privateTitlePrefix} ${title}` : title;
}

export function getCaption({
  tourStatus,
  tourStart,
  tourEnd,
  disableDoubleBookings,
  startOfSentence = true,
}: {
  tourStatus: TourStatus;
  tourStart: string;
  tourEnd: string;
  disableDoubleBookings: boolean;
  startOfSentence?: boolean;
}): string | null {
  switch (tourStatus) {
    case "active":
    case "pending":
      if (!tourStart) return null;
      return `${startOfSentence ? "S" : "s"}cheduled for ${format(
        parseISO(tourStart),
        "MMMM d, h:mm"
      )}-${format(parseISO(tourEnd), "h:mm a")}`;
    case "expired":
      return `Your ${getTourTitle({ disableDoubleBookings })} has expired.`;
    default:
      return null;
  }
}

export function getTimeForTourVerbiage({
  tourStatus,
  disableDoubleBookings,
}: {
  tourStatus: TourStatus;
  disableDoubleBookings: boolean;
}): string | null {
  return tourStatus === "active"
    ? `It's time for your ${getTourTitle({ disableDoubleBookings })}!`
    : null;
}

export function getDescription({
  tourStatus,
  units,
}: {
  tourStatus: TourStatus;
  units: UnitType[];
}): string | null {
  switch (tourStatus) {
    case "pending":
      if (units.every((unit) => unit.accessCode?.deviceType === "bluetooth")) {
        return `When your tour is ready, please open the Alloy Pass app to access your future ${pluralize(
          "home",
          units.length
        )}.`;
      } else {
        return `When your tour is ready, this page will automatically refresh and display the codes required to access the ${pluralize(
          "home",
          units.length
        )}.`;
      }
    case "expired":
      return `Please visit our office if you would like to take another tour or apply for a lease.`;
    default:
      return null;
  }
}

export default function Intro({
  disableDoubleBookings,
  tourStatus,
  tourStart,
  tourEnd,
  units,
  prospectFirstName,
}: IntroProps) {
  const timeForTour = getTimeForTourVerbiage({
    tourStatus,
    disableDoubleBookings,
  });
  const caption = getCaption({
    tourStatus,
    tourStart,
    tourEnd,
    disableDoubleBookings,
  });
  const description = getDescription({ tourStatus, units });
  return (
    <View
      style={{
        flexDirection: "column",
      }}
    >
      {prospectFirstName ? (
        <Typography type="title" style={styles.welcome}>
          Welcome, {prospectFirstName}!
        </Typography>
      ) : (
        <AnimatedPlaceholder
          style={{
            width: 188,
            height: 24,
            backgroundColor: "#E4E6EB",
          }}
        />
      )}
      {caption && (
        <Typography type="title5" color="primary">
          {caption}
        </Typography>
      )}
      {timeForTour && <Typography type="title3">{timeForTour}</Typography>}
      {description && (
        <Typography style={styles.description}>{description}</Typography>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  welcome: {
    marginBottom: 8,
  },
  description: {
    paddingBottom: 16,
  },
});
